import React, { useContext, useEffect } from 'react';
import { Route as RouterRoute, Redirect } from 'react-router-dom';

import { AuthContext } from '../context/Auth/AuthContext';
import { usePlanPermissions } from '../context/PlanPermissionsContext';
import ColorModeContext from '../layout/themeContext';
import AppLoadingScreen from '../components/AppLoadingScreen';
import { enterPublicRoute } from '../services/authSession';

const RBAC_FALLBACK = '/no-permission';

/** Avisa a sessão que uma rota pública está aberta enquanto ela estiver na tela. */
const PublicRouteMark = ({ children }) => {
  useEffect(() => enterPublicRoute(), []);
  return children;
};

const Route = ({
  component: Component,
  render,
  isPrivate = false,
  isPublic = false,
  allowedProfiles,
  permission,
  ownerOnly = false,
  requirePlanInternalChat = false,
  ...rest
}) => {
  const { isAuth, loading, user } = useContext(AuthContext);
  const { internalChat: planInternalChat, loading: planLoading } = usePlanPermissions();
  const { colorMode } = useContext(ColorModeContext);
  const themeReady = colorMode?.themeReady ?? true;

  // God mode RBAC: só owner e super bypassam permissions (admin comum respeita).
  const isGodMode = Boolean(user?.super) || Boolean(user?.isOwner);

  const hasAllowedProfile = () => {
    if (!Array.isArray(allowedProfiles) || allowedProfiles.length === 0) return true;
    const profile = (user?.profile || '').toLowerCase();
    const normalizedAllowed = allowedProfiles.map((p) => String(p).toLowerCase());
    if (normalizedAllowed.includes(profile)) return true;
    if (user?.super && normalizedAllowed.includes('superadmin')) return true;
    return false;
  };

  // Rotas owner-only (ex: financeiro/assinatura): só dono da conta e super.
  const hasOwnerAccess = () => {
    if (!ownerOnly) return true;
    return Boolean(user?.super) || Boolean(user?.isOwner);
  };

  const hasInternalChatPlanAccess = () => {
    if (!requirePlanInternalChat) return true;
    if (user?.super) return true;
    return Boolean(planInternalChat);
  };

  const hasPermission = () => {
    if (!permission) return true;
    if (!user) return false;
    if (isGodMode) return true;
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    return permissions.includes(permission);
  };

  // Rotas públicas (landing, login, cadastro, etc) - carrega instantaneamente sem loading
  if (isPublic) {
    if (
      !loading &&
      isAuth &&
      (rest.path === '/' ||
        rest.path === '/login' ||
        rest.path === '/signup' ||
        rest.path === '/cadastro')
    ) {
      return (
        <Redirect to={{ pathname: '/tickets/conversation', state: { from: rest.location } }} />
      );
    }
    return (
      <PublicRouteMark>
        <RouterRoute {...rest} component={render ? undefined : Component} render={render} />
      </PublicRouteMark>
    );
  }

  if (loading || !themeReady || (requirePlanInternalChat && planLoading)) {
    return <AppLoadingScreen message="Entrando no sistema..." />;
  }

  if (!isAuth && isPrivate) {
    return <Redirect to={{ pathname: '/login', state: { from: rest.location } }} />;
  }

  if (!isAuth && !isPrivate) {
    return <Redirect to={{ pathname: '/login', state: { from: rest.location } }} />;
  }

  if (isPrivate && !hasAllowedProfile()) {
    return <Redirect to={{ pathname: RBAC_FALLBACK, state: { from: rest.location } }} />;
  }

  if (isPrivate && !hasOwnerAccess()) {
    return <Redirect to={{ pathname: RBAC_FALLBACK, state: { from: rest.location } }} />;
  }

  if (isPrivate && !hasInternalChatPlanAccess()) {
    return <Redirect to={{ pathname: RBAC_FALLBACK, state: { from: rest.location } }} />;
  }

  if (isPrivate && !hasPermission()) {
    return <Redirect to={{ pathname: RBAC_FALLBACK, state: { from: rest.location } }} />;
  }

  return <RouterRoute {...rest} component={render ? undefined : Component} render={render} />;
};

export default Route;
